import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Form } from '@unform/web';
import { parse, format, setSeconds } from 'date-fns';
import { isEmpty, isArray } from 'lodash';
import * as Yup from 'yup';
import { Spinner } from '~/components';
import {
  Input,
  DateTime,
  Select,
  Hidden,
  Autocomplete,
  ComboboxMultiple,
} from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import api from '~/services/api';
import { Container } from './styles';
import { MODULO_CUSTOMIZACAO_RUMO } from '~/constants/modules';

export default function FimPitStop({
  data,
  reference,
  close,
  cancelarAcao,
  finalizaGravacao,
}) {
  const { client } = useSelector((state) => state.user.profile);
  const inputRef = React.useRef(null);
  const [initials, setInitials] = React.useState({});
  const [idatendimento, setIdatendimento] = React.useState(null);
  const [co, setCo] = React.useState(null);
  const [idposto, setIdposto] = React.useState(null);
  const [atendeu, setAtendeu] = React.useState('S');
  const modules = useSelector((state) => state.user.modules);
  const clienteRumo = modules.includes(MODULO_CUSTOMIZACAO_RUMO);

  const optionsAtendimento = useMemo(
    () => [
      { value: 'S', label: t('s.geral.opcoes.sim') },
      { value: 'N', label: t('s.geral.opcoes.nao') },
      { value: 'X', label: t('s.geral.opcoes.na') },
    ],
    []
  );

  const cellsUser = useMemo(
    () => [
      {
        id: 'codigousuario',
        label: t('s.cadastro.usuario.codigousuario'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeusuario',
        label: t('s.cadastro.usuario.nomeusuario'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  const options = useMemo(
    () => [
      { value: 'S', label: t('s.geral.opcoes.sim') },
      { value: 'N', label: t('s.geral.opcoes.nao') },
    ],
    []
  );

  const formatedDate = (timestamp) => {
    if (typeof timestamp === 'string') {
      return timestamp;
    }
    const date =
      timestamp === null || typeof timestamp === 'undefined'
        ? new Date()
        : parse(timestamp, 'yyyy-MM-dd HH:mm:ss.S', new Date());
    const dateWithZeroSeconds = setSeconds(date, 0);
    return format(dateWithZeroSeconds, 'dd/MM/yyyy HH:mm:ss');
  };

  const formatSemSegundos = (timestamp) => {
    const date = setSeconds(timestamp, 0);
    return format(date, 'dd/MM/yyyy HH:mm:ss');
  };

  useEffect(() => {
    async function handleCarregaAtendimento(idatendimento) {
      const { data } = await api.get(`/pitstop/${idatendimento}`);
      setIdatendimento(data.idatendimento);
      setCo(data.codigoatendimento);
      setIdposto(data.idposto);
      return true;
    }
    if (data != null) {
      handleCarregaAtendimento(data.idatendimento);
    }
  }, [data]);

  useEffect(() => {
    async function handleCarregaBanheiro(idatendimento) {
      const { data } = await api.get(`/pitstop/banheiro/${idatendimento}`);
      setInitials(data);
      reference.current.setData(data);
    }
    if (idatendimento) {
      handleCarregaBanheiro(idatendimento);
    }
  }, [idatendimento]);

  async function handleSubmit(param) {
    try {
      const schemaS = Yup.object().shape({
        datainicioatendimentobanheiro: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio')),
        datafimatendimentobanheiro: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio')),
        aguabanheiro: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        sanitariobanheiro: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        observacaobanheiro: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });
      const schemaN = Yup.object().shape({
        atendeuatendimentobanheiro: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        justificativas: Yup.array().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        observacaobanheiro: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });
      const schemaX = Yup.object().shape({
        atendeuatendimentobanheiro: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        observacaobanheiro: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });
      const schema = Yup.object().shape({
        atendeuatendimentobanheiro: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
      });
      if (param.atendeuatendimentobanheiro === 'S') {
        await schemaS.validate(param, {
          abortEarly: false,
        });
      } else if (param.atendeuatendimentobanheiro === 'N') {
        await schemaN.validate(param, {
          abortEarly: false,
        });
      } else if (param.atendeuatendimentobanheiro === 'X') {
        await schemaX.validate(param, {
          abortEarly: false,
        });
      } else {
        await schema.validate(param, {
          abortEarly: false,
        });
      }
      const objSend = {
        ...initials,
        ...param,
        datainicioatendimentobanheiro:
          param.datainicioatendimentobanheiro ||
          initials.datainicioatendimentobanheiro,
        datafimatendimentobanheiro:
          param.datafimatendimentobanheiro ||
          initials.datafimatendimentobanheiro,
        aguabanheiro: param.aguabanheiro || initials.aguabanheiro,
        sanitariobanheiro:
          param.sanitariobanheiro || initials.sanitariobanheiro,
        idcliente: client,
        justificativas: param.justificativas || [],
      };
      if (objSend.idatendimentobanheiro) {
        await api.put('pitstop/banheiro', objSend);
      } else {
        await api.post('pitstop/banheiro', objSend);
      }
      await close();
      toast.success(t('s.geral.sucessoalteracao'));
    } catch (err) {
      await finalizaGravacao();
      if (err && isArray(err)) {
        toast.error(err[0]);
      }
      await cancelarAcao();
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      {isEmpty(initials) ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Container>
              <Hidden name="idatendimentobanheiro" />
              <Hidden name="idatendimento" defaultValue={idatendimento} />
              <Hidden name="idposto" defaultValue={idposto} />
              <Input
                name="codigoatendimento"
                label={t('s.cadastro.pitstop.codigoatendimento')}
                disabled={true}
                defaultValue={co}
              />
              <Select
                name="atendeuatendimentobanheiro"
                label={t('s.cadastro.pitstop.atendeuatendimento')}
                options={optionsAtendimento}
                inputRef={inputRef}
                style={{ marginTop: 8 }}
                getValue={(value) => setAtendeu(value)}
              />
              {clienteRumo === false && (
                <div style={{ marginTop: 8, marginBottom: 10 }}>
                  <Autocomplete
                    name="codigousuario"
                    label="nomecodigousuario"
                    url="usuarios"
                    unique="codigousuario"
                    title={t('s.operacao.abastecimento.codigousuario')}
                    disableClearable={true}
                    params={{
                      filterusrposto: idposto,
                      edicao: Boolean(initials?.idatendimentobanheiro),
                    }}
                    style={{ marginTop: 8, marginBottom: 10 }}
                  />
                </div>
              )}
              {atendeu === 'S' && (
                <>
                  <DateTime
                    label={t('s.geral.opcoes.datahorainicial')}
                    name="datainicioatendimentobanheiro"
                    serialize={(value) =>
                      value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                    }
                    time
                    nullable
                    style={{ marginTop: 8 }}
                    inputRef={inputRef}
                  />
                  <DateTime
                    label={t('s.geral.opcoes.datahorafinal')}
                    name="datafimatendimentobanheiro"
                    serialize={(value) =>
                      value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                    }
                    time
                    nullable
                    style={{ marginTop: 16 }}
                  />
                  <Select
                    name="aguabanheiro"
                    label={t('s.cadastro.pitstop.aguabanheiro')}
                    options={options}
                    style={{ marginTop: 12 }}
                  />
                  <Select
                    name="sanitariobanheiro"
                    label={t('s.cadastro.pitstop.sanitariobanheiro')}
                    options={options}
                    style={{ marginTop: 8 }}
                  />
                </>
              )}
              {atendeu === 'N' && (
                <>
                  <ComboboxMultiple
                    name="justificativas"
                    label="descricaoobservacao"
                    url="pitstop/justificativa"
                    unique="idobservacaoatendimento"
                    title={t(
                      's.relatorio.alteracaomodooperacional.justificativa'
                    )}
                    params={{
                      wherefield: ['tipoatendimento'],
                      typewherefield: ['EQUALS'],
                      valuewherefield: ['5'],
                      pesquisalista: ['true'],
                    }}
                    style={{ marginBottom: 12 }}
                  />
                </>
              )}
              <Input
                name="observacaobanheiro"
                label={t('s.cadastro.pitstop.observacaoatendimento')}
                multiline
                rows={3}
                inputProps={{ maxLength: 500 }}
                style={{ marginTop: 10 }}
              />
            </Container>
          </Form>
        </>
      )}
    </>
  );
}
